// Patch 27 · home search: typing placeholder, "Near me" and a full-screen search with recent searches.
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

// patch 46: the search opens on the map (loaded only when needed — the map library is heavy)
const SearchMapOverlay = lazy(() => import("./SearchMapOverlay"));

/** Placeholder that "types" each phrase, pauses, deletes it and moves to the next. */
function useTyping(phrases) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setTxt(phrases[0]);
      return;
    }
    let i = 0, n = 0, dir = 1, id;
    const step = () => {
      const p = phrases[i % phrases.length];
      n += dir;
      setTxt(p.slice(0, n));
      let wait = dir > 0 ? 55 : 28;
      if (dir > 0 && n >= p.length) { dir = -1; wait = 1500; }
      else if (dir < 0 && n <= 0) { dir = 1; i += 1; wait = 300; }
      id = setTimeout(step, wait);
    };
    id = setTimeout(step, 500);
    return () => clearTimeout(id);
  }, [phrases]);
  return txt;
}

export function useNearMe() {
  const { t } = useI18n();
  const navigate = useNavigate();
  return () => {
    if (!navigator.geolocation) return navigate("/finder");
    navigator.geolocation.getCurrentPosition(
      (p) => navigate(`/finder?near=${p.coords.latitude.toFixed(3)},${p.coords.longitude.toFixed(3)}`),
      () => toast.error(t.h27.near_denied),
      { timeout: 10000, maximumAge: 600000 }
    );
  };
}

export default function HomeSearch() {
  const { t } = useI18n();
  const H = t.h27;
  const typed = useTyping(H.type);
  const [open, setOpen] = useState(null);   // null | "search" | "near"
  const close = useCallback((how) => {
    setOpen(null);
    if (!how) window.history.back();   // closed with ✕ / Esc: drop the history step the search added
  }, []);
  return (
    <>
      <div className="mt-6 flex items-center gap-2 bg-[#141418] border border-amber-500/30 pl-4 pr-2 py-2 max-w-xl" data-testid="home-search">
        <button type="button" onClick={() => setOpen("search")} className="flex-1 min-w-0 flex items-center gap-3 text-left py-1.5" aria-label={H.search_label} data-testid="home-search-open">
          <Search className="w-5 h-5 text-amber-400 shrink-0" />
          <span className="text-[15px] text-stone-400 truncate">{typed}<span className="tier-caret">|</span></span>
        </button>
        <button type="button" onClick={() => setOpen("near")} className="btn-gold shrink-0 inline-flex items-center gap-1.5 font-bold text-[12px] tracking-[0.06em] uppercase px-3 py-2.5" data-testid="home-near">
          <MapPin className="w-4 h-4" /> {H.near}
        </button>
      </div>
      {open && (
        <Suspense fallback={<div className="fixed inset-0 z-[80] bg-[#0A0A0C]" data-testid="search-overlay-loading" />}>
          <SearchMapOverlay onClose={close} startNear={open === "near"} />
        </Suspense>
      )}
    </>
  );
}
